<!-- Console action feedback 0.1.155; 2026-10-28 18:11:13 CST; parent 44dbda7a05c960e3c79c6c322d676ac5e4982b20. -->
<!-- Account password setup V1.0; 2026-10-07 13:29:02 CST; parent 98f2abb400ae82aeb79450073a06825fca745614. -->
<!-- HotX Console 0.1.121 首次改密会话修复。实际修改时间：2026-09-29 13:25:25 CST；精确父基线：2b265b8475709018e4bf7099ec6a081871eb4c6b；变更：CONSOLE-HOME-DATA-PASSWORD-20260929。 -->
<script setup lang="ts">
import { ElMessage } from 'element-plus';
import { computed, onMounted, reactive, ref } from 'vue';
import AccountPasswordSetup from './AccountPasswordSetup.vue';
import { inDelegationContext } from '../auth/delegationSession';
import EmptyState from '@hotx-cdn/shared/components/EmptyState.vue';
import { CONSOLE_SESSION_CHANGED_EVENT, consoleApi, hasConsoleSession } from '../api/client';
import { useAuthStore } from '../stores/auth';
import { displayDateTime, displayText } from '../utils/formatters';

const account = ref<unknown>(null);
const error = ref('');
const success = ref('');
const password = reactive({ currentPassword: '', newPassword: '' });
const email = reactive({ currentPassword: '', newEmail: '', verificationCode: '' });
const delegated = inDelegationContext();
const passwordState = ref<'SET' | 'UNSET' | 'UNKNOWN'>('UNKNOWN');
const passwordSetup = ref<InstanceType<typeof AccountPasswordSetup> | null>(null);
const username = ref('');
const usernameSaving = ref(false);
const passwordSaving = ref(false);
const emailCodeSending = ref(false);
const emailSaving = ref(false);
const auth = useAuthStore();

const accountData = computed(() => account.value as {
  profile?: Record<string, unknown>;
  serviceStatus?: Record<string, unknown>;
  writeAccess?: Record<string, unknown>;
} | null);
const profile = computed(() => accountData.value?.profile ?? {});
const serviceStatus = computed(() => accountData.value?.serviceStatus ?? {});
const writeAccess = computed(() => accountData.value?.writeAccess ?? {});

onMounted(async () => {
  if (!hasConsoleSession()) return;
  try {
    await loadAccount();
    username.value = String((accountData.value?.profile?.username ?? accountData.value?.profile?.customerName) || '');
  } catch (e) {
    error.value = e instanceof Error ? e.message : '账户信息加载失败';
  }
});

async function loadAccount() {
  if (auth.mustChangePassword) {
    account.value = { profile: await consoleApi.account.profile() };
    return;
  }
  account.value = await consoleApi.portal.account();
}

async function changePassword() {
  if (delegated || passwordState.value === 'UNSET') return;
  if (passwordSaving.value) return;
  passwordSaving.value = true;
  try {
    error.value = '';
    success.value = '';
    const token = await consoleApi.account.changePassword({ ...password });
    auth.applyToken(token);
    window.dispatchEvent(new Event(CONSOLE_SESSION_CHANGED_EVENT));
    let accountRefreshFailed = false;
    try {
      await loadAccount();
    } catch {
      accountRefreshFailed = true;
    }
    success.value = !hasConsoleSession()
      ? '密码已更新，但新会话验证未通过；请使用新密码重新登录。'
      : accountRefreshFailed
        ? '密码已更新，当前登录会话已同步；账户资料暂未刷新，可稍后重试。'
        : '密码已更新，当前登录会话已同步。';
    password.currentPassword = '';
    password.newPassword = '';
    if (success.value) ElMessage.success(success.value);
  } catch (e) {
    error.value = e instanceof Error ? e.message : '密码修改失败';
    ElMessage.error(error.value);
  } finally {
    passwordSaving.value = false;
  }
}

const statusLabels: Record<string, string> = {
  ENABLED: '已启用', ACTIVE: '运行正常', RUNNING: '运行正常',
  PENDING_PLAN: '等待开通套餐', PENDING: '等待处理',
  DISABLED: '已禁用', PAUSED: '已暂停', SUSPENDED: '已暂停',
  EXPIRED: '已到期', CANCELLED: '已取消', CANCELED: '已取消'
};

function statusText(value: unknown) {
  const key = String(value ?? '').trim().toUpperCase();
  return statusLabels[key] || displayText(value);
}

async function saveUsername() {
  usernameSaving.value = true;
  error.value = '';
  success.value = '';
  try {
    const value = username.value.trim();
    await consoleApi.account.updateUsername(value);
    account.value = await consoleApi.portal.account();
    username.value = String((accountData.value?.profile?.username ?? accountData.value?.profile?.customerName) || value);
    window.dispatchEvent(new Event(CONSOLE_SESSION_CHANGED_EVENT));
    success.value = '用户名已更新，顶部账户名称已同步。';
    if (success.value) ElMessage.success(success.value);
  } catch (e) {
    error.value = e instanceof Error ? e.message : '用户名修改失败';
    ElMessage.error(error.value);
  } finally {
    usernameSaving.value = false;
  }
}

async function sendEmailCode() {
  if (emailCodeSending.value || emailSaving.value || delegated || passwordState.value === 'UNSET') return;
  emailCodeSending.value = true; success.value = '';
  try {
    error.value = '';
    await consoleApi.account.sendEmailCode(email);
    success.value = '邮箱验证码已发送，请查收。';
    if (success.value) ElMessage.success(success.value);
  } catch (e) {
    error.value = e instanceof Error ? e.message : '验证码发送失败';
    ElMessage.error(error.value);
  } finally { emailCodeSending.value = false; }
}

async function changeEmail() {
  if (emailSaving.value || emailCodeSending.value || delegated || passwordState.value === 'UNSET') return;
  emailSaving.value = true; success.value = '';
  try {
    error.value = '';
    const token = await consoleApi.account.changeEmail({ ...email });
    auth.applyToken(token);
    window.dispatchEvent(new Event(CONSOLE_SESSION_CHANGED_EVENT));
    email.currentPassword = ''; email.verificationCode = ''; email.newEmail = '';
    success.value = '邮箱已更新，登录会话已同步。';
    try { await loadAccount(); await passwordSetup.value?.load(); } catch { success.value += '账户资料暂未刷新，可稍后重试。'; }
    if (success.value) ElMessage.success(success.value);
  } catch (e) {
    error.value = e instanceof Error ? e.message : '邮箱修改失败';
    ElMessage.error(error.value);
  } finally { emailSaving.value = false; }
}

const text = displayText;
</script>

<template>
  <section class="page">
    <div class="page-title">
      <h1>账户中心</h1>
      <p>统一查看账户资料、安全设置与服务状态。</p>
    </div>
    <el-alert
      v-if="auth.mustChangePassword"
      title="首次登录请先设置新密码；保存成功后当前登录会话会自动更新。"
      type="warning"
      show-icon
    />
    <el-alert v-if="error" :title="error" type="error" show-icon />
    <el-alert v-if="success" :title="success" type="success" show-icon />
    <EmptyState v-if="!account" :title="hasConsoleSession() ? '暂无账户数据' : '当前未登录'" :description="hasConsoleSession() ? '' : '登录后可查看账户资料、安全设置和服务状态。'" />
    <template v-else>
      <div class="account-workbench">
        <div class="account-main">
          <div class="account-grid">
            <el-card>
              <template #header>个人资料</template>
              <el-descriptions :column="2" border>
                <el-descriptions-item label="用户名">{{ text(profile.username || profile.customerName) }}</el-descriptions-item>
                <el-descriptions-item label="登录邮箱">{{ text(profile.email) }}</el-descriptions-item>
                <el-descriptions-item label="账户状态">{{ statusText(profile.accountStatus) }}</el-descriptions-item>
                <el-descriptions-item label="最近登录">{{ displayDateTime(profile.lastLoginAt) }}</el-descriptions-item>
              </el-descriptions>
            </el-card>
            <el-card>
              <template #header>服务状态</template>
              <el-descriptions :column="2" border>
                <el-descriptions-item label="当前套餐">{{ text(serviceStatus.planName) }}</el-descriptions-item>
                <el-descriptions-item label="服务状态">{{ statusText(serviceStatus.serviceStatus || profile.serviceStatus) }}</el-descriptions-item>
                <el-descriptions-item label="开通时间">{{ displayDateTime(serviceStatus.serviceStartAt) }}</el-descriptions-item>
                <el-descriptions-item label="到期时间">{{ displayDateTime(serviceStatus.serviceExpireAt) }}</el-descriptions-item>
                <el-descriptions-item label="域名额度">
                  {{ text(serviceStatus.rootDomainUsed) }} / {{ text(serviceStatus.rootDomainLimit) }}
                </el-descriptions-item>
                <el-descriptions-item label="写入权限">{{ text(writeAccess.message) }}</el-descriptions-item>
              </el-descriptions>
            </el-card>
          </div>
        </div>

        <el-card class="side-guide-card account-guide-panel">
          <template #header>账户安全建议</template>
          <ul class="guide-list">
            <li>忘记密码时使用邮箱验证码重置，不需要看到旧密码。</li>
            <li>修改邮箱前请确认新邮箱可接收验证码。</li>
            <!-- 客服联系方式展示优化，更新时间：2026-09-04 CST。
                 页面不直接暴露客服账号；点击按钮在新窗口打开联系方式，保留当前控制台操作。 -->
            <li>
              无法收到验证码时，请
              <el-button
                tag="a"
                href="https://t.me/baichen8888"
                target="_blank"
                rel="noopener noreferrer"
                type="primary"
                plain
                size="small"
              >联系我们</el-button>
            </li>
          </ul>
        </el-card>
      </div>
    </template>
    <el-card class="form-card">
      <template #header>设置用户名</template>
      <p class="status-help">用户名会显示在账户顶部，也可用于登录；必须保持唯一。</p>
      <el-input v-model="username" maxlength="64" placeholder="请输入用户名" />
      <el-button type="primary" :loading="usernameSaving" @click="saveUsername">保存用户名</el-button>
    </el-card>
    <el-card class="form-card">
      <template #header>{{ passwordState === 'UNSET' ? '设置登录密码' : '修改密码' }}</template>
      <AccountPasswordSetup ref="passwordSetup" :disabled="delegated" @state="passwordState = $event" />
      <template v-if="!delegated && passwordState !== 'UNSET'">
      <el-input v-model="password.currentPassword" type="password" autocomplete="current-password" show-password placeholder="当前密码" />
      <el-input v-model="password.newPassword" type="password" autocomplete="new-password" show-password placeholder="新密码" />
      <el-button type="primary" :loading="passwordSaving" :disabled="passwordSaving" @click="changePassword">保存密码</el-button>
      </template>
    </el-card>
    <el-card class="form-card">
      <template #header>修改邮箱</template>
      <p v-if="delegated" class="status-help">请由客户本人登录后修改邮箱。</p>
      <template v-else-if="passwordState === 'UNSET'">
        <p class="status-help">请先验证当前绑定邮箱并设置登录密码，再验证新邮箱完成更换。</p>
        <el-button @click="passwordSetup?.open()">先设置登录密码</el-button>
      </template>
      <template v-else>
      <p class="status-help">请输入平台登录密码，并验证新邮箱。没有设置过或忘记密码，可先通过邮箱验证设置。</p>
      <el-button @click="passwordSetup?.open()">邮箱验证设置密码</el-button>
      <el-input v-model="email.currentPassword" type="password" show-password placeholder="当前密码" />
      <el-input v-model="email.newEmail" placeholder="新邮箱" />
      <div class="inline-control">
        <el-input v-model="email.verificationCode" placeholder="验证码" />
        <el-button :loading="emailCodeSending" :disabled="emailCodeSending || emailSaving" @click="sendEmailCode">{{ emailCodeSending ? '发送中…' : '获取验证码' }}</el-button>
      </div>
      <el-button type="primary" :loading="emailSaving" :disabled="emailSaving || emailCodeSending" @click="changeEmail">{{ emailSaving ? '保存中…' : '更换邮箱' }}</el-button>
      </template>
    </el-card>

  </section>
</template>
